import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/shared/hooks/useToast';
import { api } from '@/lib/api';
import { Trash2 } from 'lucide-react';

type WorkspaceRole = 'owner' | 'admin' | 'member' | 'viewer';

interface Member {
  userId: string;
  role: WorkspaceRole;
  joinedAt: string | null;
  invitedAt: string;
  invitedBy: string | null;
  user?: {
    id: string;
    name: string;
    email: string;
    avatarUrl: string | null;
  };
}

interface TeamMemberListProps {
  workspaceId: string;
  members: Member[];
  currentUserId: string;
  currentUserRole: WorkspaceRole;
}

export function TeamMemberList({
  workspaceId,
  members,
  currentUserId,
  currentUserRole,
}: TeamMemberListProps): React.JSX.Element {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [removingMemberId, setRemovingMemberId] = React.useState<string | null>(null);

  const updateRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: WorkspaceRole }) => {
      return api.patch(`/workspaces/${workspaceId}/members/${userId}`, { role });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId, 'members'] });
      toast({
        title: 'Success',
        description: 'Member role updated successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update member role',
        variant: 'destructive',
      });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (userId: string) => {
      return api.delete(`/workspaces/${workspaceId}/members/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId, 'members'] });
      setRemovingMemberId(null);
      toast({
        title: 'Success',
        description: 'Member removed successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to remove member',
        variant: 'destructive',
      });
    },
  });

  const handleRoleChange = (userId: string, role: WorkspaceRole) => {
    updateRoleMutation.mutate({ userId, role });
  };

  const handleRemoveMember = (userId: string) => {
    removeMemberMutation.mutate(userId);
  };

  const canManageMembers = currentUserRole === 'owner' || currentUserRole === 'admin';

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleBadgeVariant = (role: WorkspaceRole) => {
    switch (role) {
      case 'owner':
        return 'default';
      case 'admin':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Member</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Joined</TableHead>
            {canManageMembers && <TableHead className="text-right">Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.length === 0 ? (
            <TableRow>
              <TableCell colSpan={canManageMembers ? 5 : 4} className="text-center text-muted-foreground">
                No members found
              </TableCell>
            </TableRow>
          ) : (
            members.map((member) => {
              const isCurrentUser = member.userId === currentUserId;
              const isOwner = member.role === 'owner';
              const canModify = canManageMembers && !isOwner && !isCurrentUser;

              return (
                <TableRow key={member.userId}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={member.user?.avatarUrl ?? undefined} />
                        <AvatarFallback>{getInitials(member.user?.name ?? 'U')}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{member.user?.name ?? 'Unknown'}</div>
                        {isCurrentUser && (
                          <span className="text-xs text-muted-foreground">(You)</span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{member.user?.email ?? 'N/A'}</TableCell>
                  <TableCell>
                    {canModify ? (
                      <Select
                        value={member.role}
                        onValueChange={(value) => handleRoleChange(member.userId, value as WorkspaceRole)}
                        disabled={updateRoleMutation.isPending}
                      >
                        <SelectTrigger className="w-[130px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="member">Member</SelectItem>
                          <SelectItem value="viewer">Viewer</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant={getRoleBadgeVariant(member.role)}>
                        {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {member.joinedAt
                      ? new Date(member.joinedAt).toLocaleDateString()
                      : 'Pending'}
                  </TableCell>
                  {canManageMembers && (
                    <TableCell className="text-right">
                      {canModify ? (
                        <AlertDialog
                          open={removingMemberId === member.userId}
                          onOpenChange={(open) => setRemovingMemberId(open ? member.userId : null)}
                        >
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={removeMemberMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remove Member</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to remove {member.user?.name ?? 'this member'} from the workspace?
                                This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleRemoveMember(member.userId)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Remove
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {isOwner ? 'Owner' : 'You'}
                        </span>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
